import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";

import apiUser from "../../../redux2/user/apiUser";
import Loading from "../../../components/Loading";

const SendPromotionalSMS = ({
  openSendPromotionalSMS,
  setOpenSendPromotionalSMS,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({
    message: "",
    phone: "",
  });
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [errors, setErrors] = useState({
    message: "",
    phone: "",
  });

  const handlePhoneInputChange = (e) => {
    const value = e.target.value;
    setForm({ ...form, phone: value });

    if (errors.phone) {
      setErrors({ ...errors, phone: "" });
    }

    if (value.includes(",")) {
      const newNumbers = value
        .split(",")
        .map((num) => num.trim())
        .filter((num) => num);
      const lastNumber = newNumbers.pop();

      const validNumbers = newNumbers.filter((num) => /^\d{10,12}$/.test(num));
      setPhoneNumbers((prev) => [...new Set([...prev, ...validNumbers])]);
      setForm({ ...form, phone: lastNumber || "" });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const trimmedValue = form.phone.trim();
      if (trimmedValue && /^\d{10,12}$/.test(trimmedValue)) {
        setPhoneNumbers((prev) => [...new Set([...prev, trimmedValue])]);
        setForm({ ...form, phone: "" });
        setErrors({ ...errors, phone: "" });
      } else if (trimmedValue) {
        setErrors({
          ...errors,
          phone: "Please enter a valid phone number (10-12 digits)",
        });
      }
    } else if (
      e.key === "Backspace" &&
      !form.phone &&
      phoneNumbers.length > 0
    ) {
      setPhoneNumbers((prev) => prev.slice(0, -1));
    }
  };

  const removePhoneNumber = (numberToRemove) => {
    setPhoneNumbers((prev) => prev.filter((num) => num !== numberToRemove));
  };

  const validateForm = () => {
    const newErrors = {
      message: "",
      phone: "",
    };

    if (form.message.trim() === "") {
      newErrors.message = "Please enter a message";
    }

    const allPhoneNumbers = [...phoneNumbers];
    if (form.phone.trim() && /^\d{10,12}$/.test(form.phone.trim())) {
      allPhoneNumbers.push(form.phone.trim());
    } else if (form.phone.trim()) {
      newErrors.phone = "Please enter a valid phone number (10-12 digits)";
    }

    if (allPhoneNumbers.length === 0) {
      newErrors.phone = "Please enter at least one valid phone number";
    }

    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    const allPhoneNumbers = [...phoneNumbers];
    if (form.phone.trim() && /^\d{10,12}$/.test(form.phone.trim())) {
      allPhoneNumbers.push(form.phone.trim());
    }

    const data = {
      phoneNumbers: allPhoneNumbers,
      message: form.message.trim(),
    };

    try {
      await apiUser.sendUserPromotionSMS(data);
      toast.success("SMS sent successfully");
      setForm({
        message: "",
        phone: "",
      });
      setPhoneNumbers([]);
      setErrors({
        message: "",
        phone: "",
      });
      setOpenSendPromotionalSMS(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to send SMS");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMessageChange = (e) => {
    setForm({ ...form, message: e.target.value });
    if (errors.message) {
      setErrors({ ...errors, message: "" });
    }
  };

  const handleClose = () => {
    setOpenSendPromotionalSMS(false);
    setForm({
      message: "",
      phone: "",
    });
    setPhoneNumbers([]);
    setErrors({
      message: "",
      phone: "",
    });
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Modal
      show={openSendPromotionalSMS}
      onHide={handleClose}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      className="modal-right"
    >
      <Modal.Body className="p-0">
        <div className="px-8 py-4 bg-white rounded-lg">
          <div className="mb-8 text-center">
            <h2 className="mb-2 text-2xl font-bold text-gray-900">
              Send Promotional SMS
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="phone"
                className="block mb-2 text-sm font-medium text-gray-900"
              >
                Phone Numbers
              </label>
              <div
                className={`min-h-[50px] px-3 py-2 w-full rounded-lg border transition-colors focus-within:ring-2 bg-white ${
                  errors.phone
                    ? "border-red-300 focus-within:ring-red-500 focus-within:border-red-500"
                    : "border-gray-300 focus-within:ring-blue-500 focus-within:border-blue-500"
                }`}
              >
                <div className="flex flex-wrap gap-2 items-center">
                  {phoneNumbers.map((number, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 text-sm text-blue-800 bg-blue-100 rounded-full border border-blue-200"
                    >
                      {number}
                      <button
                        type="button"
                        className="ml-2 text-blue-600 hover:text-blue-800 focus:outline-none"
                        onClick={() => removePhoneNumber(number)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    id="phone"
                    className="flex-1 min-w-[200px] outline-none border-none bg-transparent"
                    placeholder={
                      phoneNumbers.length === 0
                        ? "Enter phone numbers"
                        : "Add more numbers..."
                    }
                    onChange={handlePhoneInputChange}
                    onKeyDown={handleKeyDown}
                    value={form.phone}
                  />
                </div>
              </div>
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
              )}
              <div className="mt-2 space-y-1">
                <p className="text-sm text-gray-500">
                  Press Enter or comma to add phone numbers. Use format:
                  251XXXXXXXXX or 09XXXXXXXX
                </p>
                <p className="text-sm text-gray-500">
                  {phoneNumbers.length > 0 && (
                    <span className="font-medium text-blue-600">
                      {phoneNumbers.length} number
                      {phoneNumbers.length !== 1 ? "s" : ""} added
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div>
              <label
                htmlFor="message"
                className="block mb-2 text-sm font-medium text-gray-900"
              >
                Message
              </label>
              <textarea
                id="message"
                className={`px-4 py-3 w-full rounded-lg border transition-colors resize-none focus:ring-2 ${
                  errors.message
                    ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                }`}
                onWheel={(e) => e.target.blur()}
                name="message"
                placeholder="Enter your promotional message here..."
                onChange={handleMessageChange}
                value={form.message}
                required
                rows={4}
              />
              {errors.message && (
                <p className="mt-1 text-sm text-red-600">{errors.message}</p>
              )}
            </div>
          </form>
        </div>
      </Modal.Body>

      <Modal.Footer className="px-8 py-4 bg-gray-50 border-t">
        <div className="flex gap-3 justify-end w-full">
          <button
            type="button"
            className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-200 transition-colors font-medium"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-2.5 text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? "Sending..." : "Send SMS"}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default SendPromotionalSMS;
